import { Routes, Route, BrowserRouter, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import ScrollToTop from "./components/ScrollToTop";
import { screenTitle } from "./lib/screenTitle";
import HubGate from "components/HubGate";
import AskSamiSelection from "components/AskSamiSelection";
import NotFound from "components/NotFound";
import { Toaster } from "components/ui/toaster";
import DashboardPage from "./pages/DashboardPage";
import UploadedBomPage from "pages/UploadedBomPage";
import SearchPage from "pages/SearchPage";
import BomAnalysisPage from "pages/BomAnalysisPage";
import ShipmentPage from "pages/ShipmentPage";
import OrdersPage from "pages/OrdersPage";
import HistoryPage from "pages/HistoryPage";
import SuppliersPage from "pages/SuppliersPage";
import AnalyticsPage from "pages/AnalyticsPage";
import SettingsPage from "pages/SettingsPage";
import CartPage from "pages/CartPage";
import BomColumnMappingPage from "pages/BomColumnMappingPage";
import BomToolPage from "pages/BomToolPage";
import MyOrdersPage from "pages/MyOrdersPage";
import BomAIPage from "pages/BomAIPage";
import CompanyAdminLoginPage from "pages/CompanyAdminLoginPage";

import OrderDetailView from "components/orders/Mouser/components/OrderDetailView";
import SalesOrderHistory from "components/orders/Mouser/components/SalesOrderHistory";
import BomToolV2Page from "pages/BomToolV2Page";
import IntelligentReportPage from "pages/IntelligentReportPage";
import ProfilePage from "pages/ProfilePage";
import OnboardingPage from "pages/OnboardingPage";
import GetStartedPage from "pages/GetStartedPage";
import UserManagementPage from "pages/UserManagementPage";

// Browser tab: "<Screen> · Qbuy360". Must sit inside the router.
function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = screenTitle(pathname);
  }, [pathname]);
  return null;
}

function App() {
  // Global guard: if onboarding is flagged in sessionStorage, keep the user on
  // the onboarding route until they explicitly finish (the onboarding page
  // sets `in_onboarding` and clears it when finished).
  const OnboardingGuard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
      try {
        const inOnboarding = sessionStorage.getItem("in_onboarding");
        if (inOnboarding && location.pathname !== "/onboarding") {
          navigate("/onboarding", { replace: true });
        }
      } catch (e) {
        // ignore storage errors
      }
    }, [location.pathname, navigate]);
    return null;
  };
  // Route wrapper that prevents access to a child route while onboarding flag is set.
  const NoOnboardingRoute = ({ children }) => {
    try {
      const inOnboarding = sessionStorage.getItem("in_onboarding");
      if (inOnboarding) return <Navigate to="/onboarding" replace />;
    } catch (e) {
      // ignore
    }
    return children;
  };
  return (
    <>
    {/* Select any text → an "Ask Sami" chip hands it to the suite copilot.
        Above HubGate so it is present even on the entitlement-gate screen. */}
    <AskSamiSelection />
    <HubGate>
    <BrowserRouter>
      <ScrollToTop />
      <DocumentTitle />
      <OnboardingGuard />
      <Routes>
        {/* Sign-in, sign-up and password reset are the Semi360 Hub's (P03): HubGate
            has already sent a signed-in visitor on from these, and anyone else to
            the Hub. The redirects only catch an old bookmark or email link. */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/dashboard" replace />} />
        <Route path="/verify-email/:token" element={<Navigate to="/dashboard" replace />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="/auth/company/admin-login" element={<CompanyAdminLoginPage />} />
        <Route path="/bom-column-mapping/:id" element={<BomColumnMappingPage />} />
        <Route path="/bom-tool/:id" element={<BomToolV2Page />} />
        <Route path="/orders" element={<MyOrdersPage />} />
        <Route path="/sales-order-history" element={<SalesOrderHistory />} />
        <Route path="/my-orders/detail/:id" element={<OrderDetailView />} />
        <Route path="/dashboard" element={<NoOnboardingRoute><DashboardPage /></NoOnboardingRoute>} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/uploaded-bom" element={<UploadedBomPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/bom-analysis" element={<BomAnalysisPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/shipments" element={<ShipmentPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/bom-analysis/:id" element={<IntelligentReportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/users" element={<UserManagementPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/bom-ai" element={<BomAIPage />} />
        {/* Catch all route for 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster position="top-right" />
    </BrowserRouter>
    </HubGate>
    </>
  );
}

export default App;
